/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import utils from '../../../../common/utils';
import env from '../../../../common/env';
import * as commonConstants from '../../../../common/constants';
import { createHttpClient, HTTP_ADAPTER } from '../../../../common/http/httpClientFactory';
import TokenService from '../TokenService';

const streamingHttpClient = createHttpClient(HTTP_ADAPTER.FETCH);

/**
 * Run graph service for streaming API calls.
 * This is kept as a utility function rather than BaseService because
 * streaming APIs don't fit the standard request/response pattern.
 *
 * @param {Object} params - The parameters for running the graph
 * @param {string} params.graphId - The graph ID
 * @param {number|string} params.graphVersion - The graph version
 * @param {string} params.tenantId - The tenant ID
 * @param {Object} params.payload - The request payload
 * @param {string} [params.sys2sysToken] - Optional system-to-system token (if omitted, will be fetched)
 * @param {string} [params.threadId] - Optional thread ID for conversation continuity
 * @param {boolean} [params.useStackB] - Whether to use Stack B
 * @param {AbortSignal} [params.signal] - Optional abort signal to cancel the request
 * @returns {Promise<Response>} - The fetch response with streaming body
 */
const runGraphService = async ({
  graphId,
  graphVersion,
  tenantId,
  payload,
  sys2sysToken,
  threadId,
  useStackB = false,
  signal
}) => {
  // Run-graph requires a sys2sys token. Use provided one if available; otherwise fetch it.
  const systemToken = sys2sysToken || (await TokenService.getSys2sysToken());
  // Fallback to existing auth token to avoid breaking behavior if sys2sys fetch fails in some environments.
  const token = systemToken || utils.getToken();

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    [commonConstants.BERLIN_TENANT_ID_HEADER]: tenantId,
    // Explicitly enable streaming response (as per backend contract)
    useStream: 'true'
  };

  // Thread handling:
  // - If a threadId is provided, continue the conversation with x-thread-id
  // - Otherwise, ask backend to create a new thread
  if (!threadId) {
    headers['New-Thread'] = 'true';
  }

  // Stack B uses BERLIN_API_URI_STACKB (respects ?env= like AI Admin stack B URLs)
  const baseUrl = useStackB ? env.BERLIN_API_URI_STACKB : env.BERLIN_API_URI;
  const url = `${baseUrl}/api/1.0/run-graph/${graphId}/versions/${graphVersion}`;

  const response = await streamingHttpClient.post(url, {
    headers,
    body: JSON.stringify(payload),
    signal
  });

  if (!response.ok) {
    throw new Error(`Graph execution failed: ${response.status} ${response.statusText}`);
  }

  return response;
};

export default runGraphService;
